import type { InjectionKey, Ref } from 'vue';
import { inject, ref } from 'vue';

type FlagRef<T> = Readonly<Ref<T>>;
type LDFlagFn = <T>(flagKey: string, defaultFlagValue?: T) => FlagRef<T>;

/**
 * Injection key set by {@link setLDFlagKey} during app initialization.
 * This avoids importing `launchdarkly-vue-client-sdk` (and its heavy
 * `launchdarkly-js-client-sdk` transitive dep) in every consumer.
 */
let _ldFlagKey: InjectionKey<LDFlagFn> | null = null;

/**
 * Called once from main.ts (where the SDK is already imported) to
 * store the LD_FLAG injection key for lightweight access elsewhere.
 */
export function setLDFlagKey(key: InjectionKey<LDFlagFn>): void {
   _ldFlagKey = key;
}

/**
 * Lightweight replacement for the SDK's `useLDFlag`.
 * Must be called inside a Vue setup context (uses `inject`).
 */
export function useLDFlag<T>(flagKey: string, defaultValue?: T): FlagRef<T> {
   if (!_ldFlagKey) {
      console.warn('[useLDFlag] LD_FLAG key not set — returning default value');
      return ref(defaultValue) as Ref<T>;
   }

   const ldFlag = inject(_ldFlagKey);
   if (!ldFlag) {
      throw new Error('LaunchDarkly plugin not installed. Ensure LDPlugin is registered before using useLDFlag.');
   }

   return ldFlag(flagKey, defaultValue);
}
